<script setup lang="ts">
import { IconChatAlt } from '#components'

export interface FilterButtonProps {
    size?: string
    icon?: Component
    iconColor?: string
    textSingular?: string
    textPlural?: string
    number?: number
    isClicked?: boolean
}

const props = withDefaults(defineProps<FilterButtonProps>(), {
    size: 'sm',
    icon: IconChatAlt,
    iconColor: 'text-gray-l-500',
    textSingular: '',
    textPlural: '',
    number: 0,
    isClicked: false
})

const isDisabled = computed<boolean>(() => props.number === 0)

const getNumberColorClass = computed<string>(() => {
    if (isDisabled.value) {
        return 'text-gray-300'
    }

    return props.isClicked ? 'text-[#005085]' : 'text-gray-l-800'
})

const getTextColorClass = computed<string>(() => {
    if (isDisabled.value) {
        return 'text-gray-300'
    }

    return props.isClicked ? 'text-[#0080C5]' : 'text-gray-l-500'
})

const getCursorClass = computed<string>(() =>
    isDisabled.value ? 'hover:cursor-not-allowed' : 'hover:cursor-pointer'
)

const getClickedClasses = computed<string>(() =>
    !isDisabled.value && props.isClicked
        ? 'border-2 border-[#22BCFF] bg-[#F0FBFF]'
        : 'border'
)

const getText = computed<string>(() =>
    props.number > 1 ? props.textPlural : props.textSingular
)

const getIconColor = computed<string>(() =>
    isDisabled.value ? 'text-gray-l-300' : (props.iconColor as string)
)
</script>

<template>
    <button
        type="button"
        class="flex flex-col gap-1 p-2 sm:p-4 text-left border-solid border-gray-200 rounded-lg box-border w-full transition ease-out duration-200"
        :class="[getCursorClass, getClickedClasses]"
    >
        <span class="flex gap-2 items-center">
            <component
                :is="icon"
                class="size-6 sm:size-8"
                :is-disabled
                :class="getIconColor"
            />
            <span
                class="font-bold text-28 tracking-light"
                :class="[getNumberColorClass]"
            >
                {{ number }}
            </span>
        </span>
        <span
            class="w-fit font-normal text-thirteen-exploitation leading-4 tracking-light"
            :class="[getTextColorClass]"
        >
            {{ getText }}
        </span>
    </button>
</template>
